import { useEffect, useState } from 'react';
import type { ContentClient, PageContent } from '@accenturecodefoundry/mfe-pot-shared-content-client';
import type { Locale } from '@accenturecodefoundry/mfe-pot-shared-i18n';

/**
 * React equivalent of the batch-fetch + re-fetch-on-locale-change pattern
 * App.tsx already uses for its own single intro block, generalized to a
 * component-local set of CMS keys. CMS content isn't reactive by default,
 * so this re-fetches whenever `locale` changes.
 */
export interface PageContents {
  content: Record<string, PageContent>;
  /**
   * The locale `content` was fetched for: `undefined` until the first fetch
   * resolves, then the locale of the latest resolved fetch. Lags the
   * `locale` argument during a language switch until the new content
   * arrives.
   *
   * That lag is what scds-picker keys need. scds-picker reads its
   * scds-picker-option children's text exactly once, in componentWillLoad,
   * so a picker must be created fresh whenever its option text changes or
   * it keeps whatever it first saw. Pickers whose options come from this
   * content are keyed `${field}-${loadedLocale ?? 'loading'}`:
   * - the field keeps each picker distinct, so React never reuses one
   *   field's picker for another (bug #186);
   * - the key changes when the content first arrives (step 1's pickers
   *   render before it -- on EKS they showed raw content keys);
   * - on a language switch it changes when the new locale's content
   *   arrives, not the moment the locale does (which would remount with
   *   the old text).
   * A picker whose option text came from another async source (e.g. i18n)
   * would need that source's own loaded signal in its key.
   */
  loadedLocale: Locale | undefined;
}

export function usePageContents(
  contentClient: ContentClient | null,
  keys: readonly string[],
  locale: Locale,
): PageContents {
  const [content, setContent] = useState<Record<string, PageContent>>({});
  const [loadedLocale, setLoadedLocale] = useState<Locale | undefined>(undefined);

  useEffect(() => {
    if (!contentClient) {
      return;
    }
    let cancelled = false;
    contentClient
      .getPageContents([...keys], locale === 'fr' ? 'fr' : 'en')
      .then((result) => {
        if (!cancelled) {
          setContent(result);
          setLoadedLocale(locale);
        }
      })
      .catch((err) => {
        console.error('Failed to load page content', err);
      });
    return () => {
      cancelled = true;
    };
    // `keys` is deliberately omitted -- every call site passes a
    // module-level constant array, so including it would only add a
    // reference-identity footgun with no real effect.
  }, [contentClient, locale]);

  return { content, loadedLocale };
}
